import {ChangePasswordButton} from '@/components/ChangePasswordButton';
import {ToggleAdminButton} from '@/components/ToggleAdminButton';
import {LabeledGridItem} from '@/components/LabeledGridItem';
import {formatUserDisplayName} from '@/utils/formatUserDisplayName';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import {StatusChip} from '@/components/StatusChip';
import {alpha, Box, Divider, Grid, Typography} from '@mui/material';
import type {Theme} from '@mui/material/styles';
import {
    ArrayField,
    BooleanField,
    Datagrid,
    Edit,
    FunctionField,
    SimpleForm,
    TextField,
    useEditContext,
} from 'react-admin';

const shellSx = (theme: Theme) => ({
    borderRadius: 0,
    border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
    boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
});

const Title = () => {
    const {record} = useEditContext();
    if (record) {
        return `Users / ${formatUserDisplayName(record)}`;
    }
    return 'Users';
};

const UserActions = () => {
    const {record} = useEditContext();
    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.5,
            }}
        >
            {record?.is_admin && (
                <StatusChip
                    intent="accent"
                    icon={<AdminPanelSettingsIcon />}
                    label="Admin"
                />
            )}
            <ChangePasswordButton />
            <ToggleAdminButton />
        </Box>
    );
};

export const UserShow = () => (
    <Edit title={<Title />} mutationMode="optimistic" actions={false}>
        <SimpleForm
            toolbar={false}
            sx={(theme) => ({
                ...shellSx(theme),
                '& .MuiCardContent-root': {
                    p: 0,
                },
            })}
        >
            <Box sx={{px: 2.5, pt: 2.5, pb: 2}}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{letterSpacing: '0.1em', fontWeight: 600}}
                >
                    Account
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mt: 0.75}}>
                    Password, admin role, and profile summary.
                </Typography>
                <Box sx={{mt: 2}}>
                    <UserActions />
                </Box>
            </Box>
            <Divider />

            <Box sx={{px: 2.5, py: 2.5}}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{letterSpacing: '0.1em', fontWeight: 600, display: 'block', mb: 2}}
                >
                    Profile
                </Typography>
                <Grid container spacing={2.5}>
                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <TextField source="first_name" label="First name" />
                    </LabeledGridItem>
                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <TextField source="last_name" label="Last name" />
                    </LabeledGridItem>
                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <TextField source="company" label="Company" />
                    </LabeledGridItem>
                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <BooleanField
                            source="is_registered_breeder"
                            label="Registered breeder"
                        />
                    </LabeledGridItem>
                </Grid>
            </Box>

            <Divider />

            <Box sx={{px: 2.5, py: 2.5}}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{letterSpacing: '0.1em', fontWeight: 600, display: 'block', mb: 2}}
                >
                    Location
                </Typography>
                <Grid container spacing={2.5}>
                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <TextField label="Suburb" source="location.suburb" />
                    </LabeledGridItem>
                    <LabeledGridItem xs={12} sm={6} md={4} sx={{width: '100%'}}>
                        <TextField label="State" source="location.state" />
                    </LabeledGridItem>
                    <LabeledGridItem xs={12} md={8} sx={{width: '100%'}}>
                        <FunctionField
                            label="Coordinates"
                            render={(user) =>
                                user.location.latitude &&
                                user.location.longitude
                                    ? `${user.location.latitude}, ${user.location.longitude}`
                                    : 'Not set'
                            }
                        />
                    </LabeledGridItem>
                </Grid>
            </Box>

            <Divider />

            <Box sx={{px: 2.5, py: 2.5}}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{letterSpacing: '0.1em', fontWeight: 600, display: 'block', mb: 2}}
                >
                    Contact
                </Typography>
                <Grid container spacing={2.5}>
                    <LabeledGridItem xs={12} sm={6} sx={{width: '100%'}}>
                        <TextField source="phone_number" label="Phone" />
                    </LabeledGridItem>
                    <LabeledGridItem xs={12} sm={6} sx={{width: '100%'}}>
                        <TextField source="email" label="Email" />
                    </LabeledGridItem>
                    <LabeledGridItem xs={12} sm={4} sx={{width: '100%'}}>
                        <BooleanField
                            label="Contact by email"
                            source="contact_preference.email"
                        />
                    </LabeledGridItem>
                    <LabeledGridItem xs={12} sm={4} sx={{width: '100%'}}>
                        <BooleanField
                            label="Contact by phone"
                            source="contact_preference.call"
                        />
                    </LabeledGridItem>
                    <LabeledGridItem xs={12} sm={4} sx={{width: '100%'}}>
                        <BooleanField
                            label="Contact by SMS"
                            source="contact_preference.sms"
                        />
                    </LabeledGridItem>
                </Grid>
            </Box>

            <Divider />

            <Box sx={{px: 2.5, py: 2.5}}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{letterSpacing: '0.1em', fontWeight: 600, display: 'block', mb: 2}}
                >
                    Dogs
                </Typography>
                <Grid container spacing={2.5}>
                    <LabeledGridItem xs={12} sm={6} sx={{width: '100%'}}>
                        <TextField source="count_male_dogs" label="Male" />
                    </LabeledGridItem>
                    <LabeledGridItem xs={12} sm={6} sx={{width: '100%'}}>
                        <TextField source="count_female_dogs" label="Female" />
                    </LabeledGridItem>
                </Grid>
            </Box>

            <Divider />

            <Box sx={{px: 2.5, py: 2.5}}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{letterSpacing: '0.1em', fontWeight: 600, display: 'block', mb: 2}}
                >
                    Licences
                </Typography>
                <ArrayField source="licenses">
                    <Datagrid
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-tableWrapper': {width: '100%'},
                        }}
                        rowClick={(id, _basePath, record) =>
                            `/licenses/${record.id}/show`
                        }
                    >
                        <TextField source="organisation" />
                        <TextField source="code" />
                        <TextField source="expiry" />
                        <TextField source="status" />
                    </Datagrid>
                </ArrayField>
            </Box>
        </SimpleForm>
    </Edit>
);
